import React, { useEffect, useRef, useState } from "react";
import "./JetGrouting.css";

// Gallery image imports — replace with your actual image files
import gallery1 from "../assets/gallery/jg-gallery-1.jpeg";
import gallery2 from "../assets/gallery/jg-gallery-2.jpeg";
import gallery3 from "../assets/gallery/jg-gallery-3.jpeg";
import gallery4 from "../assets/gallery/jg-gallery-4.jpeg";
import gallery5 from "../assets/gallery/jg-gallery-5.jpeg";
import gallery6 from "../assets/gallery/jg-gallery-6.jpeg";
import gallery7 from "../assets/gallery/jg-gallery-7.jpeg";
import gallery8 from "../assets/gallery/jg-gallery-8.jpeg";
import gallery9 from "../assets/gallery/jg-gallery-9.jpeg";
import gallery10 from "../assets/gallery/jg-gallery-10.jpeg";
import overviewImg from "../assets/gallery/jg-gallery-1.jpeg";

const GALLERY_IMAGES = [
  { src: gallery1},
  { src: gallery2},
  { src: gallery3},
  { src: gallery4},
  { src: gallery5},
  { src: gallery6},
  { src: gallery7},
  { src: gallery8},
  { src: gallery9},
  { src: gallery10},
];

/* ── Main JetGrouting Component ── */
const JetGrouting = () => {
  const [isVisible, setIsVisible] = useState({});
  const sectionRefs = useRef({});

  useEffect(() => {
    const observers = {};
    Object.keys(sectionRefs.current).forEach((key) => {
      observers[key] = new IntersectionObserver(
        ([entry]) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [key]: true }));
          }
        },
        { threshold: 0.15 }
      );
      if (sectionRefs.current[key]) {
        observers[key].observe(sectionRefs.current[key]);
      }
    });
    return () => {
      Object.values(observers).forEach((obs) => obs.disconnect());
    };
  }, []);

  const setRef = (key) => (el) => {
    sectionRefs.current[key] = el;
  };

  const methodsData = [
    {
      number: "01",
      title: "Single Fluid System (S)",
      desc: "High-pressure cement grout alone erodes and mixes with soil to form soilcrete columns, suited to general ground strengthening.",
      icon: "⬇",
    },
    {
      number: "02",
      title: "Double Fluid System (D)",
      desc: "Grout jet shrouded by compressed air increases erosion energy, producing larger diameter columns in denser soils.",
      icon: "💨",
    },
    {
      number: "03",
      title: "Triple Fluid System (T)",
      desc: "Separate water-air jet erodes soil while grout is placed independently, allowing maximum column diameter and strength control.",
      icon: "🔧",
    },
    {
      number: "04",
      title: "Soilcrete Column Formation",
      desc: "Rotating and lifting monitor creates uniform cylindrical soil-cement columns for foundations, cut-off walls, and underpinning.",
      icon: "🏗",
    },
    {
      number: "05",
      title: "Grout Mix Design & QC",
      desc: "Laboratory-verified mix proportioning with on-site batching control and unconfined compressive strength (UCS) testing of cores.",
      icon: "🔬",
    },
    {
      number: "06",
      title: "Verification & Reporting",
      desc: "Column diameter and strength verification via coring or excavation, supported by detailed jetting parameter records.",
      icon: "📋",
    },
  ];

  return (
    <div className="jg-page" id="#jet-grouting">
      {/* Top Banner */}
      <section className="jg-top-banner">
        <div className="jg-top-banner__grid" />
        <div className="jg-top-banner__glow" />
        <h1 className="jg-top-banner__title">
          Jet <span className="jg-text-accent">Grouting</span>
        </h1>
      </section>

      {/* Overview Section */}
      <section
        className={`jg-overview ${isVisible["overview"] ? "jg-animate-in" : ""}`}
        ref={setRef("overview")}
      >
        <div className="jg-overview__inner">
          <div className="jg-overview__left">
            <div className="jg-section-label">
              <span className="jg-section-label__line" />
              WHAT WE DO
            </div>
            <h2 className="jg-section-title">
              ENGINEERING
              <br />
              <span className="jg-text-accent">SOIL INTO STRUCTURE</span>
            </h2>
            <p className="jg-overview__text">
              When ground conditions demand rapid, precisely engineered strengthening, jet
              grouting delivers it. GSK Geotech's Jet Grouting service uses high-pressure
              fluid jets to erode and mix soil in-situ with cement grout, forming dense
              soilcrete columns for foundations at various site conditions.
            </p>
            <p className="jg-overview__text">
              Our experienced jet grouting team has executed works across major tunnelling,
              excavation, and underpinning projects, delivering verified column diameters
              and strengths backed by full jetting parameter documentation.
            </p>
            <div className="jg-overview__highlights">
              {[
                "Experienced jet grouting specialists",
                "Single, double & triple fluid systems",
                "End-to-end: drilling, jetting, verification",
                "Compliant with industry & regulatory standards",
              ].map((item, i) => (
                <div key={i} className="jg-overview__highlight-item">
                  <span className="jg-overview__highlight-icon">✦</span>
                  {item}
                </div>
              ))}
            </div>
          </div>
          <div className="jg-overview__right">
            <div className="jg-overview__image-block">
              <img
                src={overviewImg}
                alt="Jet Grouting Soilcrete Column Formation Works"
                className="jg-overview__image"
              />
              <div className="jg-overview__stat-card">
                <span className="jg-overview__stat-number">2+</span>
                <span className="jg-overview__stat-text">JET GROUTING<br />PROJECTS</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Methods Section */}
      <section
        className={`jg-methods ${isVisible["methods"] ? "jg-animate-in" : ""}`}
        ref={setRef("methods")}
      >
        <div className="jg-methods__inner">
          <div className="jg-section-label">
            <span className="jg-section-label__line" />
            OUR APPROACH
          </div>
          <h2 className="jg-section-title">
            JETTING
            <br />
            <span className="jg-text-accent">METHODS</span>
          </h2>
          <p className="jg-methods__subtitle">
            From single to triple fluid systems — we deploy the jet grouting technique
            best matched to each site's soil type and design requirements.
          </p>
          <div className="jg-methods__grid">
            {methodsData.map((method, i) => (
              <div
                key={i}
                className="jg-method-card"
                style={{ animationDelay: `${i * 0.1}s` }}
              >
                <div className="jg-method-card__number">{method.number}</div>
                <div className="jg-method-card__icon">{method.icon}</div>
                <h3 className="jg-method-card__title">{method.title}</h3>
                <p className="jg-method-card__desc">{method.desc}</p>
                <div className="jg-method-card__line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section
        className={`jg-process ${isVisible["process"] ? "jg-animate-in" : ""}`}
        ref={setRef("process")}
      >
        <div className="jg-process__inner">
          <div className="jg-section-label">
            <span className="jg-section-label__line" />
            HOW WE WORK
          </div>
          <h2 className="jg-section-title">
            OUR <span className="jg-text-accent">PROCESS</span>
          </h2>
          <div className="jg-process__steps">
            {[
              { step: "01", title: "Site Mobilisation", desc: "Jet grouting rig setup, grout plant positioning, safety checks and permit acquisition prior to works." },
              { step: "02", title: "Drilling to Depth", desc: "Monitor string drilled to target depth using the rig's high-pressure rotary drilling capability." },
              { step: "03", title: "Jetting & Withdrawal", desc: "Controlled rotation and lifting of the monitor injects high-pressure grout to erode and mix soil, forming the column." },
              { step: "04", title: "Parameter Monitoring", desc: "Real-time logging of jetting pressure, rotation speed, lift rate, and grout flow against design parameters." },
              { step: "05", title: "Verification & Reporting", desc: "Column diameter and strength verification via coring or trial excavation, with detailed jetting records." },
            ].map((item, i) => (
              <div key={i} className="jg-process__step">
                <div className="jg-process__step-num">{item.step}</div>
                <div className="jg-process__step-connector" />
                <div className="jg-process__step-content">
                  <h4 className="jg-process__step-title">{item.title}</h4>
                  <p className="jg-process__step-desc">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Related Projects */}
      <section
        className={`jg-projects ${isVisible["projects"] ? "jg-animate-in" : ""}`}
        ref={setRef("projects")}
      >
        <div className="jg-projects__inner">
          <div className="jg-section-label">
            <span className="jg-section-label__line" />
            TRACK RECORD
          </div>
          <h2 className="jg-section-title">
            COMPLETED JET
            <br />
            <span className="jg-text-accent">GROUTING PROJECTS</span>
          </h2>
          <div className="jg-projects__grid">
            {[
              { title: "JGP works for DTSS2 Phase 2 MH04 LK1C4", client: "Megastones Holdings Pte Ltd" },
              { title: "JGP work at Tampines VSMC Project", client: "Takenaka -L&V JV" },
            ].map((proj, i) => (
              <div key={i} className="jg-project-card">
                <h4 className="jg-project-card__title">{proj.title}</h4>
                <p className="jg-project-card__client">{proj.client}</p>
              </div>
            ))}
          </div>
          <div className="jg-projects__cta">
            <a href="/projects" className="jg-btn jg-btn--outline">
              VIEW ALL PROJECTS →
            </a>
          </div>
        </div>
      </section>

      
      {/* CTA Banner */}
      <section className="jg-cta">
        <div className="jg-cta__inner">
          <div className="jg-cta__left">
            <p className="jg-cta__label">READY TO START?</p>
            <h2 className="jg-cta__title">
              NEED JET GROUTING
              <br />
              <span className="jg-text-accent">FOR YOUR PROJECT?</span>
            </h2>
            <p className="jg-cta__desc">
              Our team is ready to mobilise. Get in touch for a detailed quote
              and consultation tailored to your project's ground strengthening requirements.
            </p>
          </div>
          <div className="jg-cta__right">
            <a href="/contact-us" className="jg-btn jg-btn--primary jg-btn--large">
              CONTACT US →
            </a>
            <div className="jg-cta__contacts">
              <span>Nedu: +65 9239 8494</span>
              <span>Suben: +65 9027 1160</span>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default JetGrouting;